import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { renderThrough, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The inline offer of a way into an account, wherever a page has to make one:
 *
 *   <AuthAffordance variant={AuthAffordanceVariant.PLAIN} onClick={openLogin}>
 *     Účet již mám, přihlásit se
 *   </AuthAffordance>
 *
 *   <AuthAffordance
 *     variant={AuthAffordanceVariant.PLAIN}
 *     render={<Link href={registrationUrl} />}
 *   >
 *     Ještě nemám vytvořen účet
 *   </AuthAffordance>
 *
 * **One affordance, three jobs.** Switching to the login modal, going to the
 * registration route, and opening the login modal from inside a form are three
 * different things to DO — and one thing to look at: a word in the running copy,
 * underlined, that a shopper can press. What it does is the tenant's, and so is
 * the element: a modal opener is a `<button>` and a route is an `<a>`, which is
 * why the element arrives through `render` rather than being decided here.
 *
 * **Nothing about auth crosses into this package.** No modal atom, no route, no
 * query threading, no copy — the affordance paints itself and spreads
 * everything else natively, `aria-haspopup` included.
 *
 * `variant` is the appearance axis, required, with no `defaultVariants`.
 *
 * Server-first: the part is a plain component, and the interactive leaf is
 * whatever the tenant renders it through.
 */

/** How loudly the offer reads in the copy around it. */
export const authAffordanceVariants = cva('', {
  variants: {
    variant: {
      /** Underlined in the page's own ink, for an offer standing on its own line. */
      plain: 'text-foreground cursor-pointer underline',
      /**
       * The brand's colour and a heavier face, un-underlining under a pointer —
       * for an offer set inside a sentence, where it has to be findable without
       * a line of its own.
       */
      accent:
        'text-primary focus-visible:ring-primary inline p-0 text-left font-medium underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2',
      /** The opt-out: a tenant painting the offer itself. */
      unstyled: '',
    },
  },
});

type AuthAffordanceVariantName = NonNullable<
  VariantProps<typeof authAffordanceVariants>['variant']
>;

export type AuthAffordanceVariant = AuthAffordanceVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const AuthAffordanceVariant = {
  /** Underlined in the page's ink. */
  PLAIN: 'plain',
  /** The brand's colour, inside a sentence. */
  ACCENT: 'accent',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, AuthAffordanceVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type AuthAffordanceVariantProps = { readonly variant: AuthAffordanceVariant };

export type AuthAffordanceProps = ComponentProps<'button'> &
  AuthAffordanceVariantProps & {
    /**
     * The element to paint instead of the neutral `<button>` — a link where the
     * offer is a route rather than an overlay.
     */
    readonly render?: RenderElement;
  };

/**
 * The offer itself.
 *
 * The neutral element is a `<button>`, because two of the three things this
 * offers are overlays and only one is a destination; `type="button"` by default
 * and overridable, since an affordance inside a form must not submit it.
 */
export const AuthAffordance = ({
  className,
  variant,
  render,
  type,
  ...props
}: AuthAffordanceProps) => {
  const resolved = clsxm(authAffordanceVariants({ variant }), className);

  return (
    renderThrough(render, 'auth-affordance', resolved, props) ?? (
      <button data-slot="auth-affordance" type={type ?? 'button'} className={resolved} {...props} />
    )
  );
};
